import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Grid,
  Divider,
  Card,
  CardContent,
  Alert,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { LocationOn as LocationOnIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const LoanApproval = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loans, setLoans] = useState([]);
  const [loanTemplates, setLoanTemplates] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [formData, setFormData] = useState({
    loanTemplateId: '',
    disbursementDate: '',
  });
  const [tabValue, setTabValue] = useState(0);
  const [nicFilter, setNicFilter] = useState('');
  const [existingLoans, setExistingLoans] = useState([]);
  const [loadingPaymentHistory, setLoadingPaymentHistory] = useState(false);
  const [loanType, setLoanType] = useState(null); // 'fresh', 'additional', 'closed'
  const getArrearsInfo = () => {
    const loansWithArrears = existingLoans.filter(
      (l) => l.closedByAdmin && l.arrearsAmount && Number(l.arrearsAmount) > 0
    );
    const totalArrears = loansWithArrears.reduce(
      (sum, l) => sum + Number(l.arrearsAmount || 0),
      0
    );
    return { loansWithArrears, totalArrears };
  };

  useEffect(() => {
    fetchLoans();
    fetchLoanTemplates();
  }, []);

  const fetchLoans = async () => {
    try {
      const response = await api.get('/admin/loans/pending');
      setLoans(response.data);
    } catch (error) {
      console.error('Error fetching loans:', error);
    }
  };

  const fetchLoanTemplates = async () => {
    try {
      const response = await api.get('/admin/loan-templates');
      setLoanTemplates(response.data.filter(t => t.active));
    } catch (error) {
      console.error('Error fetching loan templates:', error);
    }
  };

  const handleApprove = async () => {
    try {
      await api.post(`/admin/loans/${selectedLoan.id}/approve`, null, {
        params: {
          adminId: user.userId,
          loanTemplateId: formData.loanTemplateId,
          disbursementDate: formData.disbursementDate,
        },
      });
      fetchLoans();
      setOpen(false);
      setSelectedLoan(null);
      setFormData({ loanTemplateId: '', disbursementDate: '' });
      setTabValue(0);
      alert('Loan approved successfully! Bank deposit has been deducted from the customer\'s area capital.');
    } catch (error) {
      console.error('Error approving loan:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message;
      alert('Error approving loan: ' + errorMessage);
    }
  };

  const handleReject = async (loanId) => {
    if (window.confirm('Are you sure you want to reject this loan?')) {
      try {
        await api.post(`/admin/loans/${loanId}/reject`, null, {
          params: { notes: 'Rejected by admin' },
        });
        fetchLoans();
      } catch (error) {
        console.error('Error rejecting loan:', error);
        alert('Error rejecting loan');
      }
    }
  };

  const openDialog = async (loan) => {
    setSelectedLoan(loan);
    setFormData({
      loanTemplateId: '',
      disbursementDate: new Date().toISOString().split('T')[0],
    });
    setTabValue(0); // Reset to first tab
    setExistingLoans([]);
    
    // Use loan type from loan object if available, otherwise determine it
    if (loan.loanType) {
      setLoanType(loan.loanType.toLowerCase());
    } else {
      setLoanType(null);
      // Fallback: determine loan type if not set
      if (loan.customer?.id) {
        await determineLoanType(loan.customer.id);
      }
    }
    
    setOpen(true);
    
    // Fetch existing loans and payment history for this customer
    if (loan.customer?.id) {
      await fetchCustomerPaymentHistory(loan.customer.id);
    }
  };

  const determineLoanType = async (customerId) => {
    try {
      const response = await api.get(`/admin/customers/${customerId}/loans`);
      const allLoans = response.data?.loans || response.data || [];
      const loans = Array.isArray(allLoans) ? allLoans : [];
      
      // Check if customer has any loans
      if (loans.length === 0) {
        setLoanType('fresh_loan'); // No previous loans - fresh loan
        return;
      }
      
      // Check for active loans
      const hasActiveLoans = loans.some(loan => loan.status === 'ACTIVE');
      if (hasActiveLoans) {
        setLoanType('additional_loan'); // Has active loans - additional loan
        return;
      }
      
      // Check if all loans are closed
      const allClosed = loans.every(loan => loan.status === 'CLOSED');
      if (allClosed) {
        setLoanType('closed_loan_customer'); // All loans closed - loan for closed loan customer
        return;
      }
      
      // Default to fresh if unclear
      setLoanType('fresh_loan');
    } catch (error) {
      console.error('Error determining loan type:', error);
      setLoanType(null);
    }
  };
  
  const getLoanTypeLabel = (type) => {
    if (!type) return 'Unknown';
    const typeUpper = type.toUpperCase();
    if (typeUpper === 'FRESH_LOAN' || typeUpper === 'FRESH') {
      return 'Fresh Loan';
    } else if (typeUpper === 'ADDITIONAL_LOAN' || typeUpper === 'ADDITIONAL') {
      return 'Additional Loan (Existing Active Loans)';
    } else if (typeUpper === 'CLOSED_LOAN_CUSTOMER' || typeUpper === 'CLOSED') {
      return 'Loan for Closed Loan Customer';
    }
    return type;
  };
  
  const getLoanTypeColor = (type) => {
    if (!type) return 'default';
    const typeUpper = type.toUpperCase();
    if (typeUpper === 'FRESH_LOAN' || typeUpper === 'FRESH') {
      return 'success';
    } else if (typeUpper === 'ADDITIONAL_LOAN' || typeUpper === 'ADDITIONAL') {
      return 'warning';
    } else if (typeUpper === 'CLOSED_LOAN_CUSTOMER' || typeUpper === 'CLOSED') {
      return 'info';
    }
    return 'default';
  };

  const fetchCustomerPaymentHistory = async (customerId) => {
    setLoadingPaymentHistory(true);
    try {
      const response = await api.get(`/admin/customers/${customerId}/loans`);
      const loans = response.data.loans || [];
      
      // Fetch installments for each loan
      const loansWithInstallments = await Promise.all(
        loans.map(async (loan) => {
          try {
            const scheduleResponse = await api.get(`/admin/customers/${customerId}/loans/${loan.id}/schedule`);
            return {
              ...loan,
              installments: scheduleResponse.data.installments || []
            };
          } catch (error) {
            console.error(`Error fetching schedule for loan ${loan.id}:`, error);
            return {
              ...loan,
              installments: []
            };
          }
        })
      );
      
      setExistingLoans(loansWithInstallments);
    } catch (error) {
      console.error('Error fetching customer payment history:', error);
      setExistingLoans([]);
    } finally {
      setLoadingPaymentHistory(false);
    }
  };

  const getPaymentStatus = (installment) => {
    const totalDue = parseFloat(installment.totalAmount || 0);
    const paid = parseFloat(installment.paidAmount || 0);
    const balance = totalDue - paid;
    const dueDate = new Date(installment.dueDate);
    const today = new Date();
    const isOverdue = dueDate < today && installment.status !== 'PAID';
    const isLate = installment.status === 'LATE' || installment.status === 'OVERDUE';
    const isUnderpaid = balance > 0 && paid > 0;
    const isOverpaid = balance < 0;
    const isOnTime = !isOverdue && !isLate && balance === 0;
    
    if (isLate || isUnderpaid) {
      return { color: 'error', status: 'Late/Underpaid' };
    } else if (isOnTime || isOverpaid) {
      return { color: 'success', status: isOverpaid ? 'Overpaid' : 'On Time' };
    } else if (installment.status === 'PAID') {
      return { color: 'success', status: 'Paid' };
    } else {
      return { color: 'default', status: installment.status };
    }
  };

  // Filter loans by NIC
  const filteredLoans = loans.filter((loan) => {
    if (!nicFilter.trim()) return true;
    const customer = loan.customer;
    const nic = customer?.nic || '';
    return nic.toLowerCase().includes(nicFilter.toLowerCase().trim());
  });

  return (
    <Box>
      <Typography 
        variant="h4" 
        gutterBottom
        sx={{ 
          fontWeight: 700,
          color: '#0D47A1',
          mb: 3,
          fontSize: { xs: '1.75rem', sm: '2rem' },
        }}
      >
        Loan Approval
      </Typography>
      
      {/* NIC Filter */}
      <Box sx={{ mb: 3 }}>
        <TextField
          label="Filter by NIC"
          variant="outlined"
          value={nicFilter}
          onChange={(e) => setNicFilter(e.target.value)}
          placeholder="Enter NIC to search"
          sx={{ 
            minWidth: { xs: '100%', sm: 300 },
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#0D47A1',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#0D47A1',
              },
            },
          }}
        />
      </Box>
      
      <TableContainer 
        component={Paper} 
        sx={{ 
          mt: 2,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(13, 71, 161, 0.1)',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#0D47A1',
            borderRadius: 4,
          },
        }}
      >
        <Table>
          <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Loan ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Customer</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap', display: { xs: 'none', sm: 'table-cell' } }}>NIC</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Requested Amount</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
          </TableHead>
          <TableBody>
            {filteredLoans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {nicFilter ? 'No loans found matching the NIC filter.' : 'No pending loans found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredLoans.map((loan) => (
              <TableRow 
                key={loan.id}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.05)',
                  },
                }}
              >
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>{loan.id}</TableCell>
                <TableCell sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>{loan.customer?.fullName || loan.customer?.name}</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 }, display: { xs: 'none', sm: 'table-cell' } }}>{loan.customer?.nic || 'N/A'}</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>Rs {loan.requestedAmount?.toFixed(2)}</TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                  <Chip 
                    label={loan.status} 
                    size="small"
                    color={loan.status === 'PENDING' ? 'warning' : 'default'}
                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                  />
                </TableCell>
                <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, py: { xs: 1, sm: 1.5 } }}>
                  <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => openDialog(loan)}
                      sx={{ 
                        background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1B5E20 0%, #0D3E0D 100%)',
                        },
                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                        padding: { xs: '4px 8px', sm: '6px 16px' },
                      }}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => handleReject(loan.id)}
                      sx={{
                        borderColor: '#D32F2F',
                        color: '#D32F2F',
                        '&:hover': {
                          borderColor: '#B71C1C',
                          backgroundColor: 'rgba(211, 47, 47, 0.1)',
                        },
                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                        padding: { xs: '4px 8px', sm: '6px 16px' },
                      }}
                  >
                    Reject
                  </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog 
        open={open} 
        onClose={() => setOpen(false)} 
        maxWidth="lg" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 2 },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100%', sm: '90vh' },
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Review & Approve Loan - Loan #{selectedLoan?.id}</Typography>
            {selectedLoan?.loanType && (
              <Chip 
                label={getLoanTypeLabel(selectedLoan.loanType)} 
                color={getLoanTypeColor(selectedLoan.loanType)}
                sx={{ ml: 2 }}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Admin visibility: special status / arrears if any */}
          {(() => {
            const { loansWithArrears, totalArrears } = getArrearsInfo();
            if (loansWithArrears.length === 0) return null;
            return (
              <Alert severity="error" sx={{ mb: 2 }}>
                <strong>Special Status:</strong> Customer has {loansWithArrears.length} loan(s) closed by admin with outstanding arrears.
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    Total Outstanding Arrears: Rs {totalArrears.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Please review payment history before approving.
                  </Typography>
                </Box>
              </Alert>
            );
          })()}

          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
            <Tab label="Customer Details" />
            <Tab label="Payment History" />
            <Tab label="Loan Information" />
            <Tab label="Approval" />
          </Tabs>

          {/* Customer Details Tab */}
          {tabValue === 0 && selectedLoan?.customer && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Customer Information
              </Typography>
              
              {/* Highlighted Area Display */}
              {selectedLoan.customer.area && (
                <Card 
                  sx={{ 
                    mb: 3,
                    background: 'linear-gradient(135deg, #1976D2 0%, #0D47A1 100%)',
                    color: 'white',
                    boxShadow: 4,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <LocationOnIcon sx={{ fontSize: 32, color: 'white' }} />
                      <Box>
                        <Typography variant="overline" sx={{ fontSize: '0.75rem', opacity: 0.9, display: 'block', mb: 0.5 }}>
                          Customer Area
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                          {selectedLoan.customer.area.name || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}
              
              <Grid container spacing={2}>
                {/* Personal Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Personal Information
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                      <Typography variant="body2"><strong>Full Name:</strong> {selectedLoan.customer.fullName || selectedLoan.customer.name || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>NIC:</strong> {selectedLoan.customer.nic || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Birthdate:</strong> {selectedLoan.customer.birthdate ? new Date(selectedLoan.customer.birthdate).toLocaleDateString() : 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Phone Number:</strong> {selectedLoan.customer.phoneNumber || selectedLoan.customer.phone || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Address:</strong> {selectedLoan.customer.address || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Business Address:</strong> {selectedLoan.customer.businessAddress || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Grama Niladhari Division:</strong> {selectedLoan.customer.gramaNiladhariDivision || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>District:</strong> {selectedLoan.customer.district || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Occupation:</strong> {selectedLoan.customer.occupation || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Registration Period:</strong> {selectedLoan.customer.registrationPeriod || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Number of Dependents:</strong> {selectedLoan.customer.numberOfDependents || 'N/A'}</Typography>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Assets:</Typography>
                        <Typography variant="body2" sx={{ pl: 1 }}><strong>Lands:</strong> {selectedLoan.customer.assetsLands || 'N/A'}</Typography>
                        <Typography variant="body2" sx={{ pl: 1 }}><strong>Vehicles:</strong> {selectedLoan.customer.assetsVehicles || 'N/A'}</Typography>
                        <Typography variant="body2" sx={{ pl: 1 }}><strong>Other:</strong> {selectedLoan.customer.assetsOther || 'N/A'}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Spouse Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Spouse Information
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                      <Typography variant="body2"><strong>Spouse Full Name:</strong> {selectedLoan.customer.spouseFullName || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Spouse NIC:</strong> {selectedLoan.customer.spouseNic || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Spouse Phone Number:</strong> {selectedLoan.customer.spousePhoneNumber || 'N/A'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Bank Details */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Bank Details
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                      <Typography variant="body2"><strong>Bank Name:</strong> {selectedLoan.customer.bankName || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Bank Branch:</strong> {selectedLoan.customer.bankBranch || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Account Number:</strong> {selectedLoan.customer.bankAccountNumber || 'N/A'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Guarantor Details */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Guarantor Details
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                      <Typography variant="body2"><strong>Guarantor Name:</strong> {selectedLoan.customer.guarantorName || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Guarantor NIC:</strong> {selectedLoan.customer.guarantorNic || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Guarantor Address:</strong> {selectedLoan.customer.guarantorAddress || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Guarantor Business Address:</strong> {selectedLoan.customer.guarantorBusinessAddress || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Guarantor Occupation:</strong> {selectedLoan.customer.guarantorOccupation || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Guarantor Phone:</strong> {selectedLoan.customer.guarantorPhoneNumber || 'N/A'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Payment History Tab */}
          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Customer Payment History
              </Typography>
              
              {loadingPaymentHistory ? (
                <Typography>Loading payment history...</Typography>
              ) : existingLoans.length === 0 ? (
                <Alert severity="info">
                  This customer has no existing loans. This is their first loan application.
                </Alert>
              ) : (
                existingLoans.map((loan) => (
                  <Card key={loan.id} variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                        Loan #{loan.id} - {loan.status}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Amount: Rs {loan.approvedAmount?.toFixed(2) || loan.requestedAmount?.toFixed(2)}
                        </Typography>
                      {loan.closedByAdmin && loan.arrearsAmount && Number(loan.arrearsAmount) > 0 && (
                        <Chip 
                          label={`Arrears: Rs ${Number(loan.arrearsAmount).toFixed(2)}`}
                          color="error"
                          size="small"
                          sx={{ ml: 2 }}
                        />
                      )}
                      </Box>
                      
                      <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>#</TableCell>
                              <TableCell>Due Date</TableCell>
                              <TableCell>Total Due</TableCell>
                              <TableCell>Paid</TableCell>
                              <TableCell>Balance</TableCell>
                              <TableCell>Paid Date</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>Payment Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {loan.installments
                              ?.filter(inst => inst.installmentNumber > 1) // Exclude first installment
                              .sort((a, b) => a.installmentNumber - b.installmentNumber)
                              .map((installment) => {
                                const totalDue = parseFloat(installment.totalAmount || 0);
                                const paid = parseFloat(installment.paidAmount || 0);
                                const balance = totalDue - paid;
                                const paymentStatus = getPaymentStatus(installment);
                                const dueDate = new Date(installment.dueDate);
                                const today = new Date();
                                const isOverdue = dueDate < today && installment.status !== 'PAID';
                                
                                return (
                                  <TableRow 
                                    key={installment.id}
                                    sx={{
                                      backgroundColor: paymentStatus.color === 'error' 
                                        ? 'rgba(211, 47, 47, 0.1)' 
                                        : paymentStatus.color === 'success' 
                                        ? 'rgba(46, 125, 50, 0.1)' 
                                        : 'transparent',
                                      '&:hover': {
                                        backgroundColor: paymentStatus.color === 'error' 
                                          ? 'rgba(211, 47, 47, 0.2)' 
                                          : paymentStatus.color === 'success' 
                                          ? 'rgba(46, 125, 50, 0.2)' 
                                          : 'action.hover',
                                      }
                                    }}
                                  >
                                    <TableCell>{installment.installmentNumber}</TableCell>
                                    <TableCell>
                                      {dueDate.toLocaleDateString()}
                                      {isOverdue && (
                                        <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                                          (Overdue)
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>Rs {totalDue.toFixed(2)}</TableCell>
                                    <TableCell>Rs {paid.toFixed(2)}</TableCell>
                                    <TableCell>
                                      <Typography 
                                        variant="body2" 
                                        color={balance === 0 ? 'success.main' : balance > 0 ? 'error.main' : 'info.main'}
                                        fontWeight="bold"
                                      >
                                        {balance === 0 ? 'Rs 0.00' : balance > 0 ? `Rs ${balance.toFixed(2)}` : `Rs ${Math.abs(balance).toFixed(2)} (Extra)`}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      {installment.paidDate ? new Date(installment.paidDate).toLocaleDateString() : '-'}
                                    </TableCell>
                                    <TableCell>{installment.status}</TableCell>
                                    <TableCell>
                                      <Typography 
                                        variant="body2" 
                                        color={paymentStatus.color === 'error' ? 'error.main' : paymentStatus.color === 'success' ? 'success.main' : 'text.primary'}
                                        fontWeight="bold"
                                      >
                                        {paymentStatus.status}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            {(!loan.installments || loan.installments.filter(inst => inst.installmentNumber > 1).length === 0) && (
                              <TableRow>
                                <TableCell colSpan={8} align="center">
                                  <Typography variant="body2" color="text.secondary">
                                    No installments found for this loan.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      {/* Summary */}
                      {loan.installments && loan.installments.length > 0 && (() => {
                        const allInstallments = loan.installments.filter(inst => inst.installmentNumber > 1);
                        const paidCount = allInstallments.filter(inst => inst.status === 'PAID').length;
                        const lateCount = allInstallments.filter(inst => {
                          const status = getPaymentStatus(inst);
                          return status.color === 'error';
                        }).length;
                        const onTimeCount = allInstallments.filter(inst => {
                          const status = getPaymentStatus(inst);
                          return status.color === 'success';
                        }).length;
                        
                        return (
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>Payment Summary:</Typography>
                            <Typography variant="body2">
                              <strong>Total Installments:</strong> {allInstallments.length} | 
                              <span style={{ color: 'green', marginLeft: '8px' }}>On Time/Overpaid: {onTimeCount}</span> | 
                              <span style={{ color: 'red', marginLeft: '8px' }}>Late/Underpaid: {lateCount}</span> | 
                              <span style={{ marginLeft: '8px' }}>Paid: {paidCount}</span>
                            </Typography>
                          </Box>
                        );
                      })()}
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          )}

          {/* Loan Information Tab */}
          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Loan Application Details
              </Typography>
              
              {/* Loan Type Indicator */}
              {loanType && (
                <Alert 
                  severity={
                    loanType === 'fresh' ? 'info' :
                    loanType === 'additional' ? 'warning' :
                    loanType === 'closed' ? 'success' : 'info'
                  }
                  sx={{ mb: 2 }}
                >
                  <strong>Loan Type:</strong>{' '}
                  {loanType === 'fresh' && 'Fresh Loan (New Customer)'}
                  {loanType === 'additional' && 'Additional Loan (Customer has existing active loans)'}
                  {loanType === 'closed' && 'Loan for Closed Loan Customer (All previous loans are closed)'}
                </Alert>
              )}
              
              <Card variant="outlined">
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Loan ID:</strong> {selectedLoan?.id}</Typography>
                      <Typography variant="body2"><strong>Requested Amount:</strong> Rs {selectedLoan?.requestedAmount?.toFixed(2)}</Typography>
                      <Typography variant="body2"><strong>Status:</strong> {selectedLoan?.status}</Typography>
                      <Typography variant="body2"><strong>Created Date:</strong> {selectedLoan?.createdAt ? new Date(selectedLoan.createdAt).toLocaleDateString() : 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Officer:</strong> {selectedLoan?.officer?.fullName || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Documents Verified:</strong> {selectedLoan?.documentsVerified ? 'Yes' : 'No'}</Typography>
                      <Typography variant="body2"><strong>Verification Comments:</strong> {selectedLoan?.verificationComments || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Notes:</strong> {selectedLoan?.notes || 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Approval Tab */}
          {tabValue === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Loan Approval
              </Typography>
              
              {/* Loan Type Indicator in Approval Tab */}
              {(selectedLoan?.loanType || loanType) && (
                <Alert 
                  severity={
                    (selectedLoan?.loanType || loanType)?.toUpperCase() === 'FRESH_LOAN' ? 'success' :
                    (selectedLoan?.loanType || loanType)?.toUpperCase() === 'ADDITIONAL_LOAN' ? 'warning' :
                    (selectedLoan?.loanType || loanType)?.toUpperCase() === 'CLOSED_LOAN_CUSTOMER' ? 'info' : 'info'
                  }
                  sx={{ mb: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <strong>Loan Type:</strong>
                    <Chip 
                      label={getLoanTypeLabel(selectedLoan?.loanType || loanType)} 
                      color={getLoanTypeColor(selectedLoan?.loanType || loanType)}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {(selectedLoan?.loanType || loanType)?.toUpperCase() === 'FRESH_LOAN' && 
                      'This is a fresh loan for a newly registered customer with no previous loans.'}
                    {(selectedLoan?.loanType || loanType)?.toUpperCase() === 'ADDITIONAL_LOAN' && 
                      'This is an additional loan. The customer has existing active loans. Previous active loans will be closed when this loan is approved.'}
                    {(selectedLoan?.loanType || loanType)?.toUpperCase() === 'CLOSED_LOAN_CUSTOMER' && 
                      'This is a loan for a customer whose all previous loans are closed. This is a fresh new loan application.'}
                  </Typography>
                </Alert>
              )}
              
              <TextField
                fullWidth
                select
                label="Loan Template"
                required
                value={formData.loanTemplateId}
                onChange={(e) => setFormData({ ...formData, loanTemplateId: e.target.value })}
                margin="normal"
              >
                {loanTemplates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name} - Rs {template.loanAmount?.toFixed(2)} ({template.numberOfInstallments} installments)
                  </MenuItem>
                ))}
              </TextField>
              {formData.loanTemplateId && (() => {
                const selectedTemplate = loanTemplates.find(t => t.id === parseInt(formData.loanTemplateId));
                return selectedTemplate ? (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2"><strong>Loan Amount:</strong> Rs {selectedTemplate.loanAmount?.toFixed(2)}</Typography>
                    <Typography variant="body2"><strong>Document Charges:</strong> Rs {selectedTemplate.documentCharges?.toFixed(2)}</Typography>
                    <Typography variant="body2"><strong>First Installment:</strong> Rs {selectedTemplate.firstInstallment?.toFixed(2)}</Typography>
                    <Typography variant="body2"><strong>Installment Amount:</strong> Rs {selectedTemplate.installmentAmount?.toFixed(2)}</Typography>
                    <Typography variant="body2"><strong>Bank Deposit:</strong> Rs {selectedTemplate.bankDeposit?.toFixed(2)}</Typography>
                    <Typography variant="body2"><strong>Number of Installments:</strong> {selectedTemplate.numberOfInstallments}</Typography>
                  </Box>
                ) : null;
              })()}
              <TextField
                fullWidth
                label="Disbursement Date"
                type="date"
                required
                value={formData.disbursementDate}
                onChange={(e) => setFormData({ ...formData, disbursementDate: e.target.value })}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpen(false); setTabValue(0); }}>Cancel</Button>
          <Button 
            onClick={() => setTabValue(tabValue - 1)} 
            disabled={tabValue === 0}
            sx={{ mr: 'auto' }}
          >
            Previous
          </Button>
          {tabValue < 3 && (
            <Button onClick={() => setTabValue(tabValue + 1)} variant="outlined">
              Next
            </Button>
          )}
          {tabValue === 3 && (
            <Button 
              onClick={handleApprove} 
              variant="contained" 
              color="success" 
              disabled={!formData.loanTemplateId || !formData.disbursementDate}
            >
              Approve Loan
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoanApproval;

